import { DatePipe } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    OnDestroy,
    output,
    Signal,
    signal,
    ChangeDetectionStrategy
} from '@angular/core';
import { customThemeQuartz } from '@app/ag-grid-custom-theme';
import { ColumnTypes } from '@models/enums/ColumnTypes';
import { Order } from '@services/filter-state.service';
import { GridService } from '@services/grid.service';
import {
    CellValueChangedEvent,
    ColDef,
    ColumnMovedEvent,
    ColumnResizedEvent,
    ColumnState,
    GetMainMenuItemsParams,
    GridApi,
    GridOptions,
    IServerSideDatasource,
    SizeColumnsToContentStrategy,
    SortChangedEvent,
    themeQuartz
} from 'ag-grid-enterprise';

import { ColumnVisibilityService } from '@app/services/column-visibility.service';
import { GridStorageService } from '@app/services/grid-storage.service';
import { parseDate } from '@utils/date-utils';
import { AgGridAngular } from 'ag-grid-angular';
import { CustomNoRowsOverlayComponent } from '../custom-no-rows-overlay/custom-no-rows-overlay.component';
import { getMenuItems } from '../grid.config';

export interface CellSorting {
    displayName?: string;
    sortBy?: Order;
}

@Component({
    selector: 'app-server-side-grid',
    templateUrl: './server-side-grid.component.html',
    styleUrls: ['./server-side-grid.component.scss'],
    providers: [DatePipe, GridStorageService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AgGridAngular]
})
export class ServerSideGridComponent implements OnDestroy {
    // Grid Configuration
    gridOptions = computed<GridOptions>(() => ({
        rowModelType: 'serverSide',
        animateRows: true,
        suppressMenuHide: true,
        cacheOverflowSize: 2,
        maxConcurrentDatasourceRequests: 1,
        infiniteInitialRowCount: 1,
        suppressAggFuncInHeader: true,
        enableBrowserTooltips: false,
        tooltipShowDelay: 0,
        tooltipHideDelay: 2000,
        suppressClickEdit: false,
        stopEditingWhenCellsLoseFocus: true,
        suppressContextMenu: true,
        enableCellTextSelection: false,
        suppressClipboardPaste: false,
        suppressHorizontalScroll: false,
        cellSelection: true,
        getRowId: this.getRowId() || undefined,
        processCellForClipboard: (params) => this.processCellForClipboard(params),
        processCellFromClipboard: (params) => this.processCellFromClipboard(params),
        onGridReady: (params) => this.onGridReady(params),
        onCellValueChanged: (event: CellValueChangedEvent) => this.onCellValueChanged(event),
        onSortChanged: (event: SortChangedEvent) => this.onSortChanged(event),
        onColumnResized: (event: ColumnResizedEvent) => this.onColumnResized(event),
        onColumnVisible: () => this.onColumnVisibilityChanged(),
        onColumnMoved: (event: ColumnMovedEvent) => this.onColumnOrderChanged(event)
    }));

    readonly columnTypes = {
        date: {
            valueFormatter: (params: any) => this.datePipe.transform(params.value, 'MM/dd/y') ?? ''
        },
        textWithTooltip: {
            tooltipValueGetter: (params: any) => {
                const value = params.value || '';
                return value.length > 50 || value.includes(' ') ? value : '';
            }
        }
    };

    theme = themeQuartz.withParams(customThemeQuartz);

    // No rows overlay configuration
    readonly CustomNoRowsOverlay = CustomNoRowsOverlayComponent;

    // Inputs
    cacheBlockSize = input<number>(20);
    maxBlocksInCache = input<number>(2);
    rowBuffer = input<number>(0);
    colDefs = input<ColDef[]>([]);
    getRowId = input<((params: any) => string) | undefined>(undefined);
    defaultColDef = input<ColDef>({
        sortable: true,
        resizable: true,
        filter: false,
        cellStyle: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        cellClassRules: {
            'editable-cell': (params) => !!params.colDef.editable
        },
        mainMenuItems: (params: GetMainMenuItemsParams) => getMenuItems(params, ['columnChooser']),
        suppressKeyboardEvent: (params) => Boolean(params.editing && params.event.key === 'Tab')
    });
    datasource = input<IServerSideDatasource>();
    appliedSort = input<{ sortBy: string; sortDirection: string }>();
    titleNoData = input<string>('');
    noRows = input<boolean>(false);
    storageKey = input<string>('');

    // Outputs
    cellValueChanged = output<CellValueChangedEvent>();
    cellSorting = output<CellSorting | null>();

    // Public computed signals
    overlayParams = computed(() => ({
        noRowsMessage: this.titleNoData() || 'No rows available'
    }));

    autoSizeStrategy: Signal<SizeColumnsToContentStrategy | undefined> = computed(() => {
        return this.gridStorageService.getGridState(this.storageKey())
            ? undefined
            : { type: 'fitCellContents', skipHeader: true, defaultMinWidth: 120, defaultMaxWidth: 300 };
    });

    // Private properties
    private gridApi?: GridApi;
    private gridReady = signal(false);

    // Injected dependencies
    private readonly datePipe = inject(DatePipe);
    private readonly gridService = inject(GridService);
    private readonly columnVisibilityService = inject(ColumnVisibilityService);
    private readonly gridStorageService = inject(GridStorageService);

    constructor() {
        this.setupNoRowsEffect();
        this.setupSortEffect();
    }

    ngOnDestroy(): void {
        this.gridService.removeGridApi();
    }

    onGridReady(params: any): void {
        this.gridApi = params.api;
        this.gridService.setGridApi(params.api);
        this.columnVisibilityService.setGridApi(params.api);

        this.gridReady.set(true);

        params.api.setGridOption('serverSideDatasource', this.datasource());

        const storedState = this.storageKey() && this.gridStorageService.getGridState(this.storageKey());
        if (storedState?.length) {
            params.api.applyColumnState({ state: storedState, applyOrder: true });
            this.columnVisibilityService.syncFromGrid();
        }
    }

    onFirstDataRendered(): void {
        if (!this.gridStorageService.getGridState(this.storageKey())) {
            this.gridService.adjustColumnWidths();
        }
    }

    onSortChanged(event: SortChangedEvent) {
        let newSort = null;
        this.gridApi?.ensureIndexVisible(0);
        if (event?.columns?.length) {
            // - If sorting stays on the same column -> columns[] has only that column
            // - If sorting moves to a different column -> columns[] includes [previous, new]
            // => Always take the last entry
            const sort = event.columns[event.columns.length - 1].getSort();
            if (sort)
                newSort = {
                    displayName: event.columns[event.columns.length - 1].getColId(),
                    sortBy: sort === 'asc' ? 'A' : 'D'
                };
        }

        this.cellSorting.emit(newSort as CellSorting);
    }

    onColumnResized(event: ColumnResizedEvent): void {
        if (this.gridApi) {
            this.gridApi.refreshHeader();

            if (this.storageKey().length && event.finished && event.source !== 'autosizeColumns') {
                this.gridStorageService.setGridState(this.storageKey(), this.gridApi.getColumnState());
            }
        }
    }

    onColumnVisibilityChanged(): void {
        this.columnVisibilityService.syncFromGrid();
        if (this.gridApi && this.storageKey().length) {
            this.gridStorageService.setGridState(this.storageKey(), this.gridApi.getColumnState());
        }
    }

    processCellForClipboard = (params: any): any => {
        const value = params.value;
        const agCellEditor = params.column.colDef.cellEditor;
        const agCellId = params.column.colId;

        if (agCellId === ColumnTypes.salesperson || agCellId === ColumnTypes.salesUsers) {
            const users = params.node.data.opportunityUsers ?? params.node.data.salesUsers ?? value;
            if (typeof users === 'string') {
                return users;
            }
            if (!Array.isArray(users) || users.length === 0) {
                return '';
            }
            return this.formatSalespersonForClipboard(users);
        }

        if (agCellId === ColumnTypes.isActive) {
            return value === true ? 'Active' : 'Archived';
        }

        if (
            agCellEditor === 'agRichSelectCellEditor' &&
            agCellId === ColumnTypes.probabilityValue &&
            value &&
            typeof value === 'object'
        ) {
            return JSON.stringify(value);
        }

        if (agCellId === ColumnTypes.divisionName) {
            const id = params.node?.data?.divisionId ?? value?.id;
            const label = value?.label ?? value?.name ?? (typeof value === 'string' ? value : '');

            return id && label ? JSON.stringify({ id, label }) : '';
        }

        return value;
    };

    processCellFromClipboard = (params: any): any => {
        const { value, column, node } = params;
        const colId = column.colId;
        const agCellEditor = column.colDef.cellEditor;
        const currentValue = node.data[colId];
        const trimmedValue = value?.trim?.() ?? '';

        if (colId === ColumnTypes.opportunityStatusName) {
            return currentValue;
        }

        if (colId === ColumnTypes.dueDate || colId === ColumnTypes.expectedResponseDate) {
            return this.processDateForClipboard(trimmedValue, currentValue);
        }

        if (colId === ColumnTypes.potentialValue || colId === ColumnTypes.proposedGrossMarginPercentage) {
            return this.processNumericFromClipboard(trimmedValue, currentValue, colId as ColumnTypes);
        }

        if (agCellEditor === 'agNumberCellEditor') {
            return trimmedValue;
        }

        if (agCellEditor === 'agRichSelectCellEditor' && colId === ColumnTypes.probabilityValue) {
            return this.processProbabilityValueForClipboard(value, currentValue, column);
        }

        if (agCellEditor === 'agRichSelectCellEditor' && colId === ColumnTypes.divisionName) {
            return this.processesDivisionNameForClipboard(trimmedValue, currentValue);
        }

        if (colId === ColumnTypes.salesperson || colId === ColumnTypes.salesUsers) {
            const previousValue = node?.data?.opportunityUsers ?? node?.data?.salesUsers ?? currentValue;
            return this.processSalespersonFromClipboard(trimmedValue, previousValue, node);
        }

        return value;
    };

    processProbabilityValueForClipboard = (value: any, currentValue: any, column: any): any => {
        const valuesList = column.colDef.cellEditorParams?.values ?? [];

        const isValidObject = (val: any): val is { value: any; id: any; label: any } =>
            val && typeof val === 'object' && 'value' in val && 'id' in val && 'label' in val;

        if (isValidObject(value)) return value;

        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (isValidObject(parsed)) return parsed;
            } catch {
                // Ignore error: value is not a valid JSON string
            }
        }

        const matched = valuesList.find((v: any) => v.value === Number.parseFloat(value));
        return matched ?? currentValue;
    };

    processesDivisionNameForClipboard = (trimmedValue: any, currentValue: any): any => {
        if (trimmedValue === '') return null;
        try {
            const parsed = JSON.parse(trimmedValue) as { id?: number; label?: string };
            if (parsed && typeof parsed === 'object' && parsed.id != null && parsed.label)
                return { id: parsed.id, label: parsed.label };
        } catch {
            // Ignore error: value is not a valid JSON string
        }
        return currentValue;
    };

    formatSalespersonForClipboard(users: any[]): string {
        const filtered = users.filter((user: any) => user?.userExternalId != null && user?.userFullName);
        return JSON.stringify(filtered);
    }

    processSalespersonFromClipboard(trimmedValue: string, currentValue: any, node: any): any {
        if (!trimmedValue || trimmedValue.toLowerCase() === 'null') {
            const empty: { userExternalId: number; userFullName: string }[] = [];
            if (node?.data) {
                node.data.opportunityUsers = empty;
                node.data.opportunitySalespersons = empty;
            }
            return empty;
        }

        try {
            const parsed = JSON.parse(trimmedValue) as { userExternalId?: number; userFullName?: string }[];
            if (!Array.isArray(parsed)) return currentValue;
            const salespersonList = parsed.filter(
                (user): user is { userExternalId: number; userFullName: string } =>
                    user?.userExternalId != null && !!user.userFullName
            );
            if (salespersonList.length === 0) return currentValue;

            if (node?.data) {
                node.data.opportunityUsers = salespersonList;
                node.data.opportunitySalespersons = salespersonList;
            }
            return salespersonList;
        } catch {
            return currentValue;
        }
    }

    processNumericFromClipboard(trimmedValue: string, currentValue: any, colId: ColumnTypes): number | null {
        if (trimmedValue === '') return null; // 0 should be valid.
        const num = Number.parseFloat(trimmedValue.replaceAll(',', ''));
        let effectiveCurrentValue = currentValue;
        if (
            colId === ColumnTypes.proposedGrossMarginPercentage &&
            effectiveCurrentValue != null &&
            typeof effectiveCurrentValue === 'number'
        ) {
            effectiveCurrentValue = effectiveCurrentValue * 100;
        }

        return Number.isNaN(num) ? effectiveCurrentValue : num;
    }

    processDateForClipboard(trimmedValue: any, currentValue: any): Date | null {
        const regex = /^\d+\.?\d*$/;
        if (trimmedValue === '' || trimmedValue.toLowerCase() === 'null') return null;
        if (regex.test(trimmedValue)) return currentValue;
        const parsed = parseDate(trimmedValue);
        return parsed ?? currentValue;
    }

    onColumnOrderChanged(event: ColumnMovedEvent) {
        if (this.gridApi && this.storageKey().length && event.finished) {
            this.gridStorageService.setGridState(this.storageKey(), this.gridApi.getColumnState());
        }
    }

    private applySort(): void {
        if (!this.gridApi) {
            return;
        }

        const sort = this.appliedSort();
        if (sort?.sortBy && sort?.sortDirection) {
            const agGridSortDirection = sort.sortDirection === 'A' ? 'asc' : 'desc';
            const columnState: ColumnState[] = [
                {
                    colId: sort.sortBy,
                    sort: agGridSortDirection
                }
            ];

            this.gridApi.applyColumnState({ state: columnState });
        }
    }

    private async onCellValueChanged(event: CellValueChangedEvent): Promise<void> {
        if (event.oldValue === event.newValue) {
            // *Values are the same, skipping...
            return;
        }

        if (!event.data || !event.colDef?.field) {
            // *'Invalid event data'
            return;
        }

        this.cellValueChanged.emit(event);
    }

    private showNoRowsOverlay(): void {
        if (this.gridApi) {
            this.gridApi.showNoRowsOverlay();
        }
    }

    private hideOverlay(): void {
        if (this.gridApi) {
            this.gridApi.hideOverlay();
        }
    }

    private setupNoRowsEffect(): void {
        effect(() => {
            if (!this.gridReady()) return;
            if (this.noRows()) {
                this.showNoRowsOverlay();
            } else {
                this.hideOverlay();
            }
        });
    }

    private setupSortEffect(): void {
        effect(() => {
            if (this.gridReady() && this.appliedSort()) {
                this.applySort();
            }
        });
    }
}
